import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}

(function () {
  // ======================
  // SETTINGS (edit later)
  // ======================
  const PROMO_ACTIVE = false;          // kapatmak için false
  const PROMO_CODE = 'SADE15';
  const PROMO_TEXT = 'İlk siparişe özel %15 indirim:';
  const PROMO_HIDE_DAYS = 30;         // kullanıcı kopyaladıysa 30 gün gizle

  function setCookie(name, value, days) {
    const d = new Date();
    d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);
    document.cookie = `${name}=${encodeURIComponent(value)}; expires=${d.toUTCString()}; path=/; SameSite=Lax`;
  }

  function getCookie(name) {
    const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    return match ? decodeURIComponent(match[2]) : null;
  }

  function formatTRY(amount) {
    // amount = number in TRY (not kuruş)
    try {
      return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(amount);
    } catch (e) {
      return amount.toFixed(0) + '₺';
    }
  }

  async function fetchCart() {
    const res = await fetch('/cart.js', { credentials: 'same-origin' });
    if (!res.ok) throw new Error('Cart fetch failed');
    return await res.json();
  }

  function ensurePromoNote() {
    const bar = document.querySelector('.sf-free-shipping[data-threshold]');
    if (!bar) return;

    // Kapatıldıysa kaldır
    let note = document.querySelector('.cart-promo-note');
    if (!PROMO_ACTIVE) {
      if (note) note.remove();
      return;
    }

    // Daha önce gizlendiyse (kopyaladıysa) promo göstermeyelim
    if (getCookie('sf_hide_sade15') === '1') {
      if (note) note.remove();
      return;
    }

    // Varsa sadece kodu güncelle
    if (note) {
      const codeEl = note.querySelector('[data-promo-code]');
      if (codeEl) codeEl.textContent = PROMO_CODE;
      return;
    }

    // Yoksa oluştur
    note = document.createElement('div');
    note.className = 'cart-promo-note';
    note.innerHTML = `
      <span>${PROMO_TEXT}</span>
      <code data-promo-code>${PROMO_CODE}</code>
      <button type="button" data-copy>kopyala</button>
    `;

    bar.after(note);

    const btn = note.querySelector('[data-copy]');
    btn?.addEventListener('click', async () => {
      const code = note.querySelector('[data-promo-code]')?.textContent?.trim() || PROMO_CODE;

      try {
        await navigator.clipboard.writeText(code);

        // Kullanıcı kopyaladıysa 30 gün gizle (tekrar görünmesin)
        setCookie('sf_hide_sade15', '1', PROMO_HIDE_DAYS);

        btn.textContent = 'kopyalandı';
        setTimeout(() => (btn.textContent = 'kopyala'), 1200);
      } catch (e) {
        // clipboard engellenirse sessiz kal
      }
    });
  }

  function updateBars(cart) {
    const bars = document.querySelectorAll('.sf-free-shipping[data-threshold]');
    if (!bars.length) {
      const note = document.querySelector('.cart-promo-note');
      if (note) note.remove();
      return;
    }

    bars.forEach(bar => {
      const threshold = parseInt(bar.getAttribute('data-threshold'), 10); // in kuruş
      const total = cart.total_price; // in kuruş

      const textEl = bar.querySelector('[data-text]');
      const fillEl = bar.querySelector('[data-fill]');
      const trackEl = bar.querySelector('.sf-free-shipping__track');

      if (!textEl || !fillEl || !threshold) return;

      const remaining = Math.max(0, threshold - total);
      const pct = Math.max(0, Math.min(100, Math.round((total / threshold) * 100)));

      if (remaining > 0) {
        const remainingTRY = remaining / 100;
        textEl.textContent = `Ücretsiz kargoya ${formatTRY(remainingTRY)} kaldı.`;
      } else {
        textEl.textContent = 'Tebrikler ücretsiz kargo aktif 🤍';
      }

      fillEl.style.width = remaining > 0 ? pct + '%' : '100%';
      if (trackEl) trackEl.setAttribute('aria-valuenow', String(remaining > 0 ? pct : 100));
    });

    // Bar güncellendikten sonra promo notu yerleştir
    ensurePromoNote();
  }

  // Debounced updater to avoid too many calls
  let t;
  function scheduleUpdate() {
    clearTimeout(t);
    t = setTimeout(async () => {
      try {
        const cart = await fetchCart();
        updateBars(cart);
      } catch (e) {
        // silently fail
      }
    }, 150);
  }

  // Initial
  document.addEventListener('DOMContentLoaded', scheduleUpdate);

  // Common theme events (some themes dispatch these)
  document.addEventListener('cart:updated', scheduleUpdate);
  document.addEventListener('cart:refresh', scheduleUpdate);

  // Fallback: listen to clicks inside cart drawer that likely change quantity/remove
  document.addEventListener('click', function (e) {
    const inCartUI =
      e.target.closest &&
      e.target.closest('[data-cart-drawer], .cart-drawer, .drawer, form[action^="/cart"]');
    if (!inCartUI) return;

    const likelyCartAction = e.target.closest(
      'button[name="plus"], button[name="minus"], button[name="updates[]"], a[href*="change"], button, a'
    );

    if (likelyCartAction) scheduleUpdate();
  });

  // Also refresh when drawer opens
  const obs = new MutationObserver(scheduleUpdate);
  obs.observe(document.documentElement, {
    subtree: true,
    attributes: true,
    attributeFilter: ['class', 'aria-hidden', 'aria-expanded'],
  });
})();

